import * as moment from 'moment';
import { Base64 } from 'js-base64';

import { IAccessToken } from '../types';

const STORAGE_KEY = 'oauth.token';

export default class AccessToken {
    data: IAccessToken.Data;
    headers: IAccessToken.Headers;
    claims: IAccessToken.Claims;

    constructor({ access_token, expires_in, refresh_token, scope, token_type }: IAccessToken.Data) {
        if (!access_token) {
            throw new Error('AccessToken not valid');
        }

        this.data = { access_token, expires_in, refresh_token, scope, token_type };

        const parts = access_token.split('.');

        this.headers = JSON.parse(Base64.decode(parts[0]));
        this.claims = JSON.parse(Base64.decode(parts[1]));
    }

    getType(): string {
        return this.headers.typ;
    }

    getAlg(): string {
        return this.headers.alg;
    }

    getToken(): string {
        return this.data.access_token;
    }

    getId(): string {
        return this.claims.jti;
    }

    getIssuer(): string {
        return this.claims.iss;
    }

    getAudience(): string {
        return this.claims.aud;
    }

    getClientId(): string {
        return this.getAudience();
    }

    getResourceOwnerId(): string {
        return this.claims.sub;
    }

    getExpires(): number {
        return this.claims.exp;
    }

    getIssuedAt(): string {
        return this.claims.iss;
    }

    getScope(): string {
        return this.claims.scope;
    }

    getScopeArray(): string[] {
        return this.claims.scope.split(' ');
    }

    getRefreshToken(): string {
        return this.data.refresh_token;
    }

    hasRefreshToken(): boolean {
        return !!this.data.refresh_token;
    }

    hasExpired(expireLeeway = 0): boolean {
        return moment.unix(this.claims.exp - expireLeeway) < moment();
    }

    isValid(expireLeeway = 0): boolean {
        return !this.hasExpired(expireLeeway);
    }

    saveToLocalStorage(storageKey: string = null) {
        window.localStorage.setItem(storageKey || STORAGE_KEY, JSON.stringify(this.data));
        return this;
    }

    removeFromLocalStorage(storageKey: string = null) {
        window.localStorage.removeItem(storageKey || STORAGE_KEY);
    }

    static getFromLocalStorage(storageKey: string = null) {
        let data: any = window.localStorage.getItem(storageKey || STORAGE_KEY);
        if (!data || data === 'undefined') {
            return undefined;
        }
        data = <IAccessToken.Data>JSON.parse(data);
        return new this(data);
    }
}
